import { useEffect, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import artifactContentCopy from '@/i18n/ns/artifact-content';
import './runtime-frame.css';

// The frame markup for a Page or Site (T1-UI1), around RUN's mountNamedRuntime.
// UI1 provides a fresh blank iframe and the states around it; RUN owns the ticket,
// the scope, the sandbox and referrer attributes, the transport and every lifecycle
// rule, and ART composes the route, authorization and build resolution. The mount
// function arrives as a prop so this file imports nothing from the server tree
// (src/apps/runtime/host-frame.mjs) and the real one is injected where ART composes.
//
// pending-contract: these structural types mirror host-frame.d.mts. When ART
// composes the frame they are replaced by that module's types.

export type RuntimeIssued = {
  readonly scope: unknown;
  readonly ticket: string;
  readonly bootstrapUrl: string;
  readonly expiresAt: string;
};
export type RuntimeHandlers = Record<
  string,
  { request?: (args: unknown, options?: unknown) => Promise<unknown> | unknown } | undefined
>;
export type MountedRuntime = Readonly<{
  transport: unknown;
  scope: unknown;
  invalidate(): void;
}>;
export type MountNamedRuntime = (options: {
  window: Window;
  iframe: HTMLIFrameElement;
  issued: RuntimeIssued;
  handlers: RuntimeHandlers;
  renew(scope: unknown): Promise<unknown>;
  retire(scope: unknown): Promise<unknown>;
  onInvalidated(): void;
}) => MountedRuntime;

type State = 'opening' | 'ready' | 'invalidated' | 'failed';

/**
 * Why the view is closed or failed, as ART/RUN's result projects it (never read from a thrown
 * error, so nothing technical leaks): one plain sentence, whether trying again can help, the
 * next step when it cannot, and a quiet technical reference for support.
 */
export type RuntimeReason = {
  text: string;
  retryable: boolean;
  next?: { label: string; onAct: () => void };
  detail?: string;
};

export function RuntimeFrame({
  mount,
  issued,
  handlers,
  renew,
  retire,
  title,
  onMounted,
  onInvalidated,
  onReady,
  onReopen,
  frameRef,
  reason,
  opening,
  inert,
}: {
  mount: MountNamedRuntime;
  /** A ticket is single use: a new one means a new frame, so it is the frame's key. */
  issued: RuntimeIssued;
  /** Must include the trusted runtime.ready handler; this frame observes it. */
  handlers: RuntimeHandlers;
  renew: (scope: unknown) => Promise<unknown>;
  retire: (scope: unknown) => Promise<unknown>;
  title: string;
  /** The transport and scope, once mounted: the inspector's frame adapter binds to it. */
  onMounted?: (mounted: MountedRuntime) => void;
  onInvalidated?: () => void;
  /** The view has reported ready (its first content is shown). */
  onReady?: () => void;
  /** Ask ART for a fresh ticket; shown only when the view has been closed. */
  onReopen?: () => void;
  /** The iframe element, for the inspector's frame adapter (DES2 useFrameInspectAdapter). */
  frameRef?: MutableRefObject<HTMLIFrameElement | null>;
  /** The reason behind a closed or failed view (from RUN's result, via ART). */
  reason?: RuntimeReason | null;
  /** What shows while the view opens, in place of the plain note; it fades as the view reports ready. */
  opening?: ReactNode;
  /** Not reachable by keyboard, pointer or assistive technology (a view opening behind the shown one). */
  inert?: boolean;
}) {
  return (
    // Keyed by the ticket: a fresh frame per issued ticket, never a navigated one.
    <MountedFrame
      key={issued.ticket}
      {...{
        mount,
        issued,
        handlers,
        renew,
        retire,
        title,
        onMounted,
        onInvalidated,
        onReady,
        onReopen,
        frameRef,
        reason,
        opening,
        inert,
      }}
    />
  );
}

function MountedFrame({
  mount,
  issued,
  handlers,
  renew,
  retire,
  title,
  onMounted,
  onInvalidated,
  onReady,
  onReopen,
  frameRef,
  reason,
  opening,
  inert,
}: Parameters<typeof RuntimeFrame>[0]) {
  const copy = useNamespaceCopy(artifactContentCopy);
  const iframe = useRef<HTMLIFrameElement>(null);
  const [state, setState] = useState<State>('opening');
  // A skeleton only if opening takes a moment: a fast open never flashes one.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), 400);
    return () => window.clearTimeout(timer);
  }, []);
  // The latest callbacks, so a parent re-render never remounts the runtime.
  const latest = useRef({ handlers, renew, retire, onMounted, onInvalidated, onReady });
  latest.current = { handlers, renew, retire, onMounted, onInvalidated, onReady };

  useEffect(() => {
    const frame = iframe.current;
    if (!frame) return;
    let handle: MountedRuntime | null = null;
    let gone = false;
    let shown = false;
    // RUN requires its trusted runtime.ready handler and rejects a mount without one. This
    // frame only observes it: when the caller supplied none, nothing is added, so RUN's
    // rejection stands (and shows as a failed view) instead of being papered over.
    const ready = latest.current.handlers['runtime.ready'];
    // Only a real request function is wrapped: a truthy ready object without one (empty, or
    // event-only) is passed on unchanged so RUN refuses it.
    const observed: RuntimeHandlers =
      typeof ready?.request === 'function'
        ? {
            ...latest.current.handlers,
            'runtime.ready': {
              ...ready,
              request: async (args, options) => {
                const result = await ready.request!(args, options);
                if (!gone) {
                  setState((was) => (was === 'opening' ? 'ready' : was));
                  if (!shown) latest.current.onReady?.();
                  shown = true;
                }
                return result;
              },
            },
          }
        : latest.current.handlers;
    try {
      handle = mount({
        window,
        iframe: frame,
        issued,
        handlers: observed,
        renew: (scope) => latest.current.renew(scope),
        retire: (scope) => latest.current.retire(scope),
        onInvalidated: () => {
          if (gone) return;
          setState('invalidated');
          latest.current.onInvalidated?.();
        },
      });
      latest.current.onMounted?.(handle);
    } catch {
      // Nothing about why leaves this component: the reason is RUN's to log.
      setState('failed');
    }
    return () => {
      gone = true;
      handle?.invalidate();
    };
    // One mount per frame: the key already tracks the ticket.
  }, []);

  return (
    <div
      className="ws-runtime-frame"
      data-state={state}
      data-testid="runtime-frame"
      inert={inert || undefined}
    >
      {/* A blank frame: RUN refuses one that has navigated. */}
      <iframe
        ref={(el) => {
          iframe.current = el;
          if (frameRef) frameRef.current = el;
        }}
        title={title}
        src="about:blank"
      />
      {opening && (state === 'opening' || state === 'ready') && (
        <div className="ws-runtime-opening" aria-hidden={state === 'ready'}>
          {opening}
        </div>
      )}
      {!opening && state === 'opening' && (
        <div className="ws-runtime-note" role="status">
          {slow && <span className="ws-runtime-skeleton" aria-hidden="true" />}
          <p>{copy.runtimeOpening}</p>
        </div>
      )}
      {(state === 'invalidated' || state === 'failed') && (
        <div className="ws-runtime-note" role="status">
          <p>{state === 'failed' ? copy.runtimeFailed : copy.runtimeInvalidated}</p>
          {reason && <p className="ws-runtime-reason">{reason.text}</p>}
          {/* Retry only when the producer positively says it can help; an unknown reason offers
              none. Otherwise the next step the caller supplied. */}
          {reason?.retryable === true && onReopen && (
            <button type="button" className="ws-picker-trigger" onClick={onReopen}>
              {copy.runtimeReload}
            </button>
          )}
          {reason && !reason.retryable && reason.next && (
            <button type="button" className="ws-picker-trigger" onClick={reason.next.onAct}>
              {reason.next.label}
            </button>
          )}
          {reason?.detail && (
            <details className="ws-runtime-details">
              <summary>{copy.runtimeDetails}</summary>
              <code>{reason.detail}</code>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
